import React, { useState, useEffect } from 'react';

import {
  Badge,
  Drawer,
  Group,
  LoadingOverlay,
  Pagination,
  Paper,
  Stack,
  Text,
  Title,
  Alert
} from '@mantine/core';

import EncounterCard from './EncounterCard';
import EncounterDetails from './EncounterDetails';
import { type Encounter } from './types';
import { usePostJSON } from '../../utils/request';

interface EncounterResponse {
  success: boolean;
  encounters: Encounter[];
  total: number;
  page: number;
  limit: number;
}

interface EncounterListProps {
  title: string;
  onRefetchReady?: (refetch: () => void) => void;
  clientId?: number;
  callerId?: number;
  who?: string;
  intent?: string;
}

const EncounterList: React.FC<EncounterListProps> = ({
  title,
  onRefetchReady,
  clientId,
  callerId,
  who,
  intent
}) => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);

  // Hook for encounters
  const {
    data,
    error,
    loading: isLoading,
    refetch
  } = usePostJSON<EncounterResponse>(
    '/api/encounter/data',
    {
      page,
      limit,
      clientId,
      callerId,
      who,
      intent
    },
    [page, limit, clientId, callerId, who, intent]
  );

  // Register refetch function with parent component
  useEffect(() => {
    if (onRefetchReady) {
      onRefetchReady(refetch);
    }
  }, [onRefetchReady, refetch]);

  const handleViewDetails = (encounter: Encounter) => {
    setSelectedEncounter(encounter);
    setDetailsDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDrawerOpen(false);
    setSelectedEncounter(null);
  };

  const totalPages = Math.ceil((data?.total || 0) / limit);
  const encounters = data?.encounters || [];
  const total = data?.total || 0;

  if (error) {
    return (
      <Paper withBorder p="md" style={{ position: 'relative' }}>
        <Alert color="red" title="Error">
          {error.message}
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />

        <Group justify="space-between" mb="md">
          <Title order={3}>{title}</Title>
          <Badge color="blue" variant="light">
            {total} {total === 1 ? 'encounter' : 'encounters'}
          </Badge>
        </Group>

        {encounters.length === 0 ? (
          <Text c="dimmed" py="xl" ta="center">
            No encounters found.
          </Text>
        ) : (
          <>
            <Stack gap="md">
              {encounters.map((encounter) => (
                <EncounterCard
                  key={encounter.id}
                  badgeSize={(clientId || callerId) ? 'md' : 'sm'}
                  encounter={encounter}
                  showCaller={!callerId}
                  showClient={!clientId}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </Stack>

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                />
              </Group>
            )}
          </>
        )}
      </div>

      {/* Encounter Details Drawer */}
      <Drawer
        opened={detailsDrawerOpen}
        position="right"
        size="lg"
        title={
          selectedEncounter ?
            `Encounter Details - ${selectedEncounter._client.firstName} ${selectedEncounter._client.lastName}` :
            'Encounter Details'
        }
        onClose={handleCloseDetails}
      >
        {selectedEncounter && (
          <EncounterDetails
            encounter={selectedEncounter}
            onClose={handleCloseDetails}
            onRefetch={refetch}
          />
        )}
      </Drawer>
    </>
  );
};

export default EncounterList;
